import React, { useEffect, useState } from 'react';
import { fetchOnboardedLayers, testOnboardingConnection, publishOnboardedLayer } from '../services/demoExtrasService';
import { canDo, getCurrentRole } from './RoleSwitcher';
import { logAudit } from '../services/demoExtrasService';

const STEPS = ['source', 'mapping', 'symbology', 'review'];

export default function OnboardingPanel() {
  const [role, setRole] = useState(getCurrentRole());
  const [step, setStep] = useState(0);
  const [layers, setLayers] = useState([]);
  const [form, setForm] = useState({
    name: '',
    source_type: 'WFS',
    source_url: '',
    geometry_type: 'Point',
    symbol_color: '#2563eb',
    field_mapping: { id: 'id', name: 'name', timestamp: 'last_updated' },
    description: '',
    tags: 'sensors, demo'
  });
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchOnboardedLayers().then(setLayers);
    const onRole = () => setRole(getCurrentRole());
    window.addEventListener('nexus-role-change', onRole);
    return () => window.removeEventListener('nexus-role-change', onRole);
  }, []);

  if (!canDo('onboard')) {
    return (
      <div style={{ padding: 24, color: '#94a3b8' }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: '#e2e8f0', marginBottom: 8 }}>Layer Onboarding</div>
        <div style={{ background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.3)', color: '#fca5a5', padding: 12, borderRadius: 6, fontSize: 13 }}>
          Access denied — your current role ({role.label}) cannot onboard new layers. Switch to Demo Admin in the header to demonstrate this flow.
        </div>
      </div>
    );
  }

  const test = async () => {
    setTesting(true); setError(''); setTestResult(null);
    try {
      const r = await testOnboardingConnection(form.source_url);
      setTestResult(r);
    } catch (e) {
      setError(e.message);
    } finally {
      setTesting(false);
    }
  };

  const publish = async () => {
    setPublishing(true); setError('');
    try {
      const payload = { ...form, tags: form.tags.split(',').map(t => t.trim()).filter(Boolean) };
      const created = await publishOnboardedLayer(payload);
      setLayers([created, ...layers]);
      logAudit({ user: role.user, role: role.id, action: 'LAYER_PUBLISHED', detail: `Published ${form.name} from ${form.source_type}`, sources: ['GIS Server'] });
      setStep(0);
      setForm({ ...form, name: '', source_url: '' });
      setTestResult(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div style={{ padding: '20px 16px', height: '100%', overflowY: 'auto', overflowX: 'hidden', background: '#0b1220', color: '#e2e8f0', boxSizing: 'border-box' }}>
      <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 4 }}>New Layer Onboarding</div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 16 }}>Connect a new data source and publish it as a queryable GIS layer.</div>

      <Stepper step={step} />

      {step === 0 && (
        <Card>
          <Field label="Layer name" placeholder="e.g. Crestwood Air Quality Sensors" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
          <Field label="Source type" select value={form.source_type} onChange={(v) => setForm({ ...form, source_type: v })} options={['WFS', 'WMS', 'ArcGIS Feature Service', 'GeoJSON URL', 'PostGIS table', 'Databricks table']} />
          <Field label="Source URL or connection string" placeholder="https://gis.example.com/geoserver/wfs?service=WFS&request=GetCapabilities" value={form.source_url} onChange={(v) => setForm({ ...form, source_url: v })} />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 12 }}>
            <button onClick={test} disabled={!form.source_url || testing} style={btnSecondary}>{testing ? 'Testing…' : 'Test connection'}</button>
            {testResult && (
              <span style={{ color: '#22c55e', fontSize: 12 }}>
                ✓ Connected · {testResult.latency_ms}ms · {testResult.preview_record_count} records · fields: {testResult.preview_fields.join(', ')}
              </span>
            )}
            {error && <span style={{ color: '#fca5a5', fontSize: 12 }}>{error}</span>}
          </div>
          <NavBar nextDisabled={!testResult || !form.name} onNext={() => setStep(1)} />
        </Card>
      )}

      {step === 1 && (
        <Card>
          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8 }}>Field mapping</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>Map source fields to platform-standard fields. Defaults shown.</div>
          {Object.entries(form.field_mapping).map(([k, v]) => (
            <div key={k} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 8 }}>
              <code style={{ flex: '0 0 90px', fontSize: 11, color: '#cbd5e1', fontFamily: 'ui-monospace, monospace' }}>{k}</code>
              <span style={{ color: '#64748b', flex: '0 0 auto' }}>→</span>
              <input
                value={v}
                onChange={(e) => setForm({ ...form, field_mapping: { ...form.field_mapping, [k]: e.target.value } })}
                style={{ ...inputStyle, minWidth: 0 }}
              />
            </div>
          ))}
          <Field label="Description" placeholder="Brief description for the layer catalog" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
          <Field label="Tags (comma-separated)" placeholder="sensors, fenceline, demo" value={form.tags} onChange={(v) => setForm({ ...form, tags: v })} />
          <NavBar onBack={() => setStep(0)} onNext={() => setStep(2)} />
        </Card>
      )}

      {step === 2 && (
        <Card>
          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8 }}>Symbology</div>
          <Field label="Geometry type" select value={form.geometry_type} onChange={(v) => setForm({ ...form, geometry_type: v })} options={['Point', 'LineString', 'Polygon', 'MultiPoint']} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 12 }}>
            <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>Symbol color</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <input type="color" value={form.symbol_color} onChange={(e) => setForm({ ...form, symbol_color: e.target.value })} style={{ width: 40, height: 30, border: 'none', borderRadius: 4, cursor: 'pointer' }} />
              <code style={{ color: '#94a3b8', fontSize: 12 }}>{form.symbol_color}</code>
            </div>
          </div>
          <NavBar onBack={() => setStep(1)} onNext={() => setStep(3)} />
        </Card>
      )}

      {step === 3 && (
        <Card>
          <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8 }}>Review &amp; publish</div>
          <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
            <tbody>
              {[
                ['Layer name', form.name],
                ['Source type', form.source_type],
                ['Source URL', form.source_url],
                ['Geometry', form.geometry_type],
                ['Color', form.symbol_color],
                ['Field mapping', Object.entries(form.field_mapping).map(([k, v]) => `${k}→${v}`).join(', ')],
                ['Tags', form.tags]
              ].map(([k, v]) => (
                <tr key={k} style={{ borderTop: '1px solid rgba(148,163,184,0.12)' }}>
                  <td style={{ padding: '8px 12px 8px 0', color: '#94a3b8', verticalAlign: 'top', whiteSpace: 'nowrap' }}>{k}</td>
                  <td style={{ padding: '8px 0', color: '#e2e8f0' }}>{v || <span style={{ color: '#64748b' }}>—</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <NavBar onBack={() => setStep(2)} onNext={publish} nextLabel={publishing ? 'Publishing…' : 'Publish layer'} nextDisabled={publishing} />
          {error && <div style={{ color: '#fca5a5', fontSize: 12, marginTop: 8 }}>{error}</div>}
        </Card>
      )}

      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600, marginBottom: 8 }}>Recently onboarded ({layers.length})</div>
        {layers.length === 0 ? (
          <div style={{ color: '#64748b', fontSize: 12 }}>No layers onboarded yet through this flow.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {layers.map((l) => (
              <div key={l.id} style={{ padding: 10, background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.18)', borderRadius: 6 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ width: 10, height: 10, borderRadius: '50%', background: l.symbol_color }} />
                  <div style={{ fontSize: 12, fontWeight: 500, color: '#e2e8f0' }}>{l.name}</div>
                  <span style={{ fontSize: 10, color: '#22c55e', marginLeft: 'auto' }}>● Published</span>
                </div>
                <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>{l.source_type} · {l.geometry_type} · {l.owner} · {new Date(l.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Stepper({ step }) {
  return (
    <div style={{ display: 'flex', gap: 4, marginBottom: 16 }}>
      {STEPS.map((s, i) => (
        <div key={s} style={{
          flex: 1, height: 4, borderRadius: 2,
          background: i <= step ? '#2563eb' : 'rgba(148,163,184,0.2)',
          transition: 'background 200ms'
        }} />
      ))}
    </div>
  );
}

function Card({ children }) {
  return <div style={{ padding: 14, background: 'rgba(15,23,42,0.6)', border: '1px solid rgba(148,163,184,0.18)', borderRadius: 8, boxSizing: 'border-box' }}>{children}</div>;
}

function Field({ label, value, onChange, placeholder, select, options }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
      <div style={{ fontSize: 11, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 }}>{label}</div>
      {select ? (
        <select value={value} onChange={(e) => onChange(e.target.value)} style={inputStyle}>
          {options.map((o) => <option key={o} value={o} style={{ color: '#0f172a' }}>{o}</option>)}
        </select>
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} style={inputStyle} />
      )}
    </div>
  );
}

function NavBar({ onBack, onNext, nextDisabled, nextLabel = 'Next' }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 16 }}>
      {onBack ? <button onClick={onBack} style={btnSecondary}>Back</button> : <span />}
      <button onClick={onNext} disabled={nextDisabled} style={btnPrimary}>{nextLabel}</button>
    </div>
  );
}

const inputStyle = { width: '100%', minWidth: 0, boxSizing: 'border-box', background: 'rgba(15,23,42,0.5)', color: '#e2e8f0', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 4, padding: '6px 10px', fontSize: 13 };
const btnPrimary = { background: '#2563eb', color: 'white', border: 'none', borderRadius: 4, padding: '8px 16px', fontSize: 13, fontWeight: 500, cursor: 'pointer' };
const btnSecondary = { background: 'transparent', color: '#cbd5e1', border: '1px solid rgba(148,163,184,0.3)', borderRadius: 4, padding: '8px 16px', fontSize: 13, cursor: 'pointer' };
